import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Link } from '@tanstack/react-router';
import { SortableHeader, Table, TextLink, cn, } from '@wit-support/ui';
import { CAMPAIGN_COLUMN_LABEL, factHint, groupCountLabel, horizonRoas, } from '../campaigns-logic.js';
import { FACT_PER_DOLLAR, columnHint } from '../glossary.js';
import { gapMinor, intRu, money, num2, pct, days, moneyWhole, purchasesRu } from '../format.js';
import { CppCell, ChanceCell, GapCell } from './money-cells.js';
import { StripCell } from './strip-cell.js';
const MUTED = 'text-[var(--color-text-muted)]';
/** Фон ячеек шапки кабинета (`!` — перекрывает фон закреплённой колонки таблицы). */
export const GROUP_CELL_BG = '!bg-[var(--color-surface-sunken)]';
/** Колонки, которые сортируются от меньшего к большему при первом клике. */
const ASC_FIRST = new Set(['name', 'gap', 'cpp']);
/** Числовые ячейки — одинаковые для кампании, кабинета и «Итого». */
function valueCell(key, g, theta, max) {
    switch (key) {
        case 'name':
            return null;
        case 'spend':
            return moneyWhole(g.spendMinorFull);
        case 'purchases':
            return intRu(g.purchases);
        case 'cpp':
            return _jsx(CppCell, { cpp: g.cpp, breakeven: g.cppBreakevenMinor });
        case 'breakeven':
            return _jsx("span", { className: MUTED, children: money(g.cppBreakevenMinor) });
        case 'roas':
            return _jsx(StripCell, { group: g, theta: theta, max: max });
        case 'chance':
            return _jsx(ChanceCell, { p: g.answer.p, verdict: g.answer.verdict });
        case 'gap':
            return _jsx(GapCell, { gap: gapMinor(g, theta), verdict: g.answer.verdict });
        case 'safe':
            return _jsx("span", { className: MUTED, children: money(g.cppSafeMinor) });
        case 'h60':
            return num2(horizonRoas(g, '60'));
        case 'h90':
            return num2(horizonRoas(g, '90'));
        case 'fact':
            return _jsx("span", { title: factHint(g), children: num2(g.factRoas) });
        case 'age':
            return days(g.avgAgeDays);
        case 'coverage':
            return pct(g.coverage);
    }
}
function headerAlign(key) {
    return key === 'name' || key === 'roas' ? 'left' : 'right';
}
/** Клик по строке, но не по ссылке внутри неё (ссылка навигирует сама). */
function rowClick(open) {
    return (e) => {
        if (e.target.closest('a'))
            return;
        open();
    };
}
/**
 * Таблица кампаний, сгруппированная по кабинетам (спека §6.2): строка группы —
 * числа кабинета целиком из отчёта, «Итого» — `total` отчёта.
 */
export function CampaignsTable({ groups, columns, sort, onSortChange, theta, stripMax, names, total, selectedId, linkFor, onOpen, }) {
    const cellClass = (key) => cn('whitespace-nowrap tabular-nums', headerAlign(key) === 'right' && 'text-right');
    const nameCell = (c) => {
        const parts = names.get(c.campaignId) ?? { dim: '', bold: c.campaignName ?? '' };
        const full = `${parts.dim}${parts.bold}`;
        return (
        // Строки должны различаться без наведения: общий префикс сжат до
        // нескольких знаков с «…» (полностью — в подсказке и для чтения с
        // экрана), отличающийся хвост переносится и виден до 2 строк.
        _jsx(TextLink, { asChild: true, tone: "inherit", className: "line-clamp-2 block max-w-[300px] whitespace-normal break-words leading-snug", children: _jsxs(Link, { ...linkFor(c.campaignId), title: full, "data-testid": "campaign-name", children: [parts.dim ? (_jsx("span", { className: cn(MUTED, 'mr-0.5 inline-block max-w-[4.5em] truncate align-bottom'), children: parts.dim })) : null, _jsx("span", { className: cn(c.campaignId === selectedId && 'font-semibold'), children: parts.bold })] }) }));
    };
    return (_jsxs(Table, { density: "compact", stickyHeader: true, className: "min-w-[900px]", children: [_jsx(Table.Header, { children: _jsx(Table.Row, { children: columns.map((col) => (_jsx(SortableHeader, { column: col, sortKey: col.id, sort: sort, onSortChange: onSortChange, align: headerAlign(col.id), defaultDirection: ASC_FIRST.has(col.id) ? 'asc' : 'desc', className: "whitespace-nowrap", title: col.id === 'roas' ? `Пунктир — порог ${num2(theta)}` : undefined, hint: columnHint(col.id, theta, FACT_PER_DOLLAR), children: CAMPAIGN_COLUMN_LABEL[col.id] }, col.id))) }) }), _jsxs(Table.Body, { children: [groups.map((g) => (_jsx(GroupRows, { group: g, columns: columns, cellClass: cellClass, theta: theta, stripMax: stripMax, selectedId: selectedId, nameCell: nameCell, onOpen: onOpen }, g.accountId))), _jsx(Table.Row, { "data-testid": "total-row", className: "font-semibold hover:bg-transparent", children: columns.map((col) => (_jsx(Table.Cell, { column: col, className: cn(cellClass(col.id), 'border-t border-[var(--color-border-strong)]'), children: col.id === 'name'
                                ? `Итого · ${purchasesRu(total.purchases)}`
                                : valueCell(col.id, total, theta, stripMax) }, col.id))) })] })] }));
}
function GroupRows({ group, columns, cellClass, theta, stripMax, selectedId, nameCell, onOpen, }) {
    const acc = group.account;
    return (_jsxs(_Fragment, { children: [_jsx(Table.Row, { "data-testid": "group-row", className: "bg-[var(--color-surface-sunken)] font-semibold hover:bg-[var(--color-surface-sunken)]", title: "\u041A\u0430\u0431\u0438\u043D\u0435\u0442 \u0446\u0435\u043B\u0438\u043A\u043E\u043C \u2014 \u0447\u0438\u0441\u043B\u0430 \u043F\u043E \u0432\u0441\u0435\u043C \u0435\u0433\u043E \u043A\u0430\u043C\u043F\u0430\u043D\u0438\u044F\u043C \u0437\u0430 \u043F\u0435\u0440\u0438\u043E\u0434", children: columns.map((col) => (
                // Фон шапки кабинета — на ячейках: закреплённые ячейки иначе
                // взяли бы фон карточки из стиля таблицы.
                _jsx(Table.Cell, { column: col, className: cn(cellClass(col.id), GROUP_CELL_BG), children: col.id === 'name' ? (_jsxs(_Fragment, { children: [group.title, ' ', _jsxs("span", { className: "font-normal text-[var(--color-text-muted)]", children: ["\u00B7 ", groupCountLabel(group)] })] })) : acc ? (valueCell(col.id, acc, theta, stripMax)) : null }, col.id))) }), group.rows.map((c) => {
                const selected = c.campaignId === selectedId;
                return (_jsx(Table.Row, { "data-testid": "campaign-row", "data-state": selected ? 'selected' : undefined, "aria-selected": selected || undefined, className: cn('cursor-pointer', c.answer.verdict === null && MUTED), onClick: rowClick(() => onOpen(c.campaignId)), children: columns.map((col) => (_jsx(Table.Cell, { column: col, className: cellClass(col.id), children: col.id === 'name' ? nameCell(c) : valueCell(col.id, c, theta, stripMax) }, col.id))) }, c.campaignId));
            })] }));
}
//# sourceMappingURL=campaigns-table.js.map